import { PaymentDetailsHeader } from "./payment-details-header";
import { PaymentDetailsPreviewProps } from "./types";
import { calculateFeesForPreview } from "./lib/calculate-fees";
import { BigNumber } from "bignumber.js";
import { formatPrice } from "./lib/format-price";
import { FeeBreakdown } from "./fee-breakdown";
import { SummaryLine } from "./summary-line";

export const NumberOfSharesPaymentDetailsPreview = ({
	title,
	investmentId,
	updatedAt,
	createdAt,
	currency = "USD",
	price = "0",
	quantity = "0",
	masterFundManagerFeesPerShare = "0",
	gstPercentage = "0",
	feeView = "simplified",
	className,
	feeDetails = [],
}: PaymentDetailsPreviewProps) => {
	const priceBN = BigNumber(price);
	const quantityBN = BigNumber(quantity);
	const masterFundManagerFeesPerShareBN = BigNumber(masterFundManagerFeesPerShare);
	const gstPercentageBN = BigNumber(gstPercentage);

	const masterFundSubtotal = priceBN
		.multipliedBy(quantityBN)
		.plus(masterFundManagerFeesPerShareBN.multipliedBy(quantityBN));

	// Fee tiers (lower and upper bounds) are based on the quantity for number of shares
	const result = calculateFeesForPreview(
		masterFundSubtotal,
		feeDetails,
		gstPercentageBN.toNumber(),
		quantityBN,
	);
	const totalFee = result.fee.toNumber();
	const gstFee = result.gstFee.toNumber();
	const totalFeeWithGst = result.totalFeeWithGst.toNumber();

	return (
		<div className={`rounded-lg border p-4 pt-0 w-[600px] ${className}`}>
			<div className="flex flex-col gap-4">
				<PaymentDetailsHeader
					title={title}
					updatedAt={updatedAt}
					investmentId={investmentId}
					createdAt={createdAt}
				/>
				<SummaryLine
					field="Price"
					value={
						<>
							<span className="font-bold">
								{formatPrice(priceBN.toNumber())} {currency.toUpperCase()}
							</span>{" "}
							/ Share
						</>
					}
				/>
				{masterFundManagerFeesPerShareBN.gt(0) && (
					<SummaryLine
						field="Master fund manager fees"
						value={
							<>
								<span className="font-bold">
									{formatPrice(masterFundManagerFeesPerShareBN.toNumber())}{" "}
									{currency.toUpperCase()}
								</span>{" "}
								/ Share
							</>
						}
					/>
				)}
				<SummaryLine
					field="Quantity"
					value={<span>{formatPrice(quantityBN.toNumber(), 0)}</span>}
				/>
				<hr />
				<SummaryLine
					field="Master fund subtotal amount"
					value={`${formatPrice(
						masterFundSubtotal.toNumber(),
					)} ${currency.toUpperCase()}`}
				/>
				<FeeBreakdown
					feeView={feeView}
					gstPercentage={gstPercentageBN.toNumber()}
					totalFees={BigNumber(totalFee)}
					gstFee={BigNumber(gstFee)}
					currency={currency}
					feeDetails={feeDetails}
				/>
				<hr />
				<div className="flex justify-between">
					<div className="text-sm font-bold text-gray-600">Total</div>
					<div className="text-sm font-bold text-[#383838]">{`${formatPrice(
						totalFeeWithGst,
					)} ${currency.toUpperCase()}`}</div>
				</div>
			</div>
		</div>
	);
};
